import {
  CheckIcon,
  ChevronDown,
  SlidersHorizontal,
  XCircle,
  XIcon,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { NewTOptions } from "@/interfaces/interfaces";
import { cn } from "@/lib/utils";
import { customToast } from "@/utils/custom-toast";

type TOptionSelector<T> = {
  statusFor: string;
  activeId: string | string[];
  optionList: NewTOptions<T>[];
  setIsOption: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      statusFor: string;
    }>
  >;
  isOptionAdditional?: () => void;
  onValueChange: (value: string | string[]) => Promise<void>;
  disabled?: boolean;
  className?: string;
  showIcon?: boolean;
  placeholder?: string;
  isMultiple?: boolean;
  config?: {
    isRequired?: boolean;
    isFinal?: boolean; // this is when the option is final and cannot be changed
  };
};

export const OptionSelector = <T,>({
  activeId,
  statusFor,
  optionList,
  setIsOption,
  onValueChange,
  disabled = false,
  showIcon,
  className,
  placeholder,
  isOptionAdditional,
  isMultiple = false,
  config = {
    isRequired: true,
  },
}: TOptionSelector<T>): React.JSX.Element => {
  const [open, setOpen] = React.useState(false);
  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [searchedText, setSearchedText] = React.useState("");

  const filterOptions = optionList?.filter((option) =>
    option?.name?.toLowerCase()?.includes(searchedText?.toLowerCase()),
  );

  const option = React.useMemo(() => {
    if (isMultiple) {
      return optionList.filter((option) =>
        selectedValues.includes(option?.id || ""),
      );
    }

    return optionList.find((option) => option.id === selectedValues[0]);
  }, [selectedValues, optionList, isMultiple]);

  // eslint-disable-next-line no-nested-ternary
  const backgroundOpacity = Array.isArray(option)
    ? option.map((o) =>
        o.details?.backgroundOpacity === 100
          ? "FF"
          : o.details?.backgroundOpacity,
      )
    : option?.details?.backgroundOpacity === 100
      ? "FF"
      : option?.details?.backgroundOpacity;

  const bgWithOpacities = Array.isArray(option)
    ? option.map((o) => o.details?.backgroundColor + backgroundOpacity)
    : (option?.details?.backgroundColor ?? "") + backgroundOpacity;

  const handleOptionChange = async (currentValue: string): Promise<void> => {
    if (currentValue === "" || config.isFinal) {
      if (config.isFinal) {
        customToast.warn("Cannot Update Finalized data.");
      }

      return;
    }
    if (isMultiple) {
      const isSelected = selectedValues.includes(currentValue);

      // Add or remove option from the selected values
      const newSelectedValues = isSelected
        ? selectedValues.filter((val) => val !== currentValue)
        : [...selectedValues, currentValue];

      setSelectedValues(newSelectedValues);
      await onValueChange(newSelectedValues);
    } else {
      const isSelected = selectedValues.includes(currentValue);
      //Show toast if the option is already selected
      if (isSelected && config.isRequired) {
        toast.warning("Option already selected");

        return;
      } else if (isSelected && !config.isRequired) {
        setSelectedValues(["null"]);
        await onValueChange("null");
      } else {
        setSelectedValues([currentValue]);
        await onValueChange(currentValue);
      }
    }
  };

  React.useEffect(() => {
    if (activeId && optionList) {
      setSelectedValues(Array.isArray(activeId) ? activeId : [activeId]);
    } else {
      setSelectedValues([]);
    }
  }, [activeId, optionList]);

  const renderContent = (): JSX.Element => {
    if (selectedValues?.length === 0) {
      return (
        <>
          <span className='ml-2 '>{placeholder || "Select Option"}</span>
          {showIcon && <ChevronDown size={18} className='mr-2' />}
        </>
      );
    }
    if (selectedValues?.length > 0 && isMultiple) {
      return (
        <div className='flex items-center justify-between w-full'>
          <div className='flex flex-wrap items-center'>
            {selectedValues?.map((val) => {
              const option = optionList.find((o) => o.id === val);
              const backgroundOpacity =
                option?.details?.backgroundOpacity === 100
                  ? "FF"
                  : option?.details?.backgroundOpacity;

              const bgWithOpacity =
                (option?.details?.backgroundColor ?? "") + backgroundOpacity;

              return (
                <Badge
                  key={val}
                  className='m-1 transition duration-300 ease-in-out delay-150 hover:scale-110'
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleOptionChange(val);
                  }}
                  style={{
                    backgroundColor: bgWithOpacity,
                    opacity: (option?.details?.textOpacity || 100) / 100,
                    color: option?.details?.textColor,
                    fontWeight: 400,
                  }}
                >
                  {option?.name?.substring(0, 25)}
                  <XCircle className='w-4 h-4 ml-2 cursor-pointer' />
                </Badge>
              );
            })}
          </div>
          <div className='flex items-center justify-between'>
            <XIcon
              className='h-4 mx-2 cursor-pointer text-muted-foreground'
              onClick={async (event) => {
                event.stopPropagation();
                setSelectedValues([]);
                await onValueChange([]);
              }}
            />
            <ChevronDown className='h-4 mx-2 cursor-pointer text-muted-foreground' />
          </div>
        </div>
      );
    }

    return (
      <span
        style={{
          opacity:
            ((option as NewTOptions<T>)?.details?.textOpacity || 100) / 100,
          color: (option as NewTOptions<T>)?.details?.textColor,
          fontWeight: 400,
        }}
        className='ml-2 truncate '
      >
        {(option as NewTOptions<T>)?.name?.substring(0, 25)}
      </span>
    );
  };

  React.useEffect(() => {
    if (selectedValues.length === 0) {
      return;
    }

    // Filter valid selected values that exist in the option list
    const validValues = selectedValues.filter((value) =>
      optionList.some((option) => option.id === value),
    );

    // Check if any selected value is not in optionList
    const selectedValuesInOptionList =
      validValues.length === selectedValues.length;

    // If there are valid values and it's a multiple selection, update the state
    if (isMultiple && validValues.length > 0 && !selectedValuesInOptionList) {
      setSelectedValues(validValues);
      //eslint-disable-next-line
      onValueChange(validValues);
    } else if (!selectedValuesInOptionList) {
      setSelectedValues([]);
      //eslint-disable-next-line
      onValueChange(isMultiple ? [] : "");
    }
  }, [selectedValues, optionList, isMultiple, onValueChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          role='combobox'
          aria-expanded={open}
          className={cn(
            selectedValues.length > 0
              ? "text-white"
              : "text-gray-400 hover:text-gray-300",
            "min-w-[200px] flex w-full p-1  rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit active:scale-100",
            disabled && "pointer-events-none",
            className,
          )}
          variant={"outline"}
          // disabled={disabled}
          style={{
            backgroundColor:
              !isMultiple && selectedValues.length === 1
                ? (bgWithOpacities as string)
                : "transparent",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {renderContent()}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn(
          "p-1",
          "popover-content-width-full max-h-64 overflow-y-auto scrollbar-thin",
        )}
      >
        <Command>
          <CommandInput
            placeholder='Search Status...'
            className='h-8'
            value={searchedText}
            onValueChange={setSearchedText}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
          <CommandEmpty>No options found.</CommandEmpty>
          <CommandGroup onClick={(e) => e.stopPropagation()}>
            {filterOptions &&
              filterOptions
                ?.sort((a, b) => a?.details.position - b?.details.position)
                .map((S) => {
                  const isSelected = selectedValues.includes(S?.id || "");

                  const backgroundOpacity =
                    S?.details?.backgroundOpacity === 100
                      ? "FF"
                      : S?.details?.backgroundOpacity;

                  const bgWithOpacity =
                    (S?.details?.backgroundColor ?? "") + backgroundOpacity;

                  //  this is for css injection
                  const escapedValue = CSS.escape(S.name ?? "");

                  return (
                    <CommandItem
                      key={S.id}
                      value={escapedValue}
                      onSelect={async () => {
                        await handleOptionChange(S?.id || "");
                        setOpen(false);
                      }}
                      className='py-2 cursor-pointer'
                      style={{
                        backgroundColor: bgWithOpacity,
                        marginTop: "4px",
                        color: "white",
                      }}
                    >
                      <span
                        style={{
                          opacity: S?.details?.textOpacity / 100,
                          color: S?.details?.textColor,
                          fontWeight: 400,
                        }}
                        className='line-clamp-1'
                      >
                        {S.name}
                      </span>
                      <CheckIcon
                        className={cn(
                          "ml-auto h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
          </CommandGroup>
          <ButtonWithIcon
            icon={<SlidersHorizontal size={16} className='mr-2' />}
            variant={"secondary"}
            className='m-1 text-xs'
            onClick={(e) => {
              e.stopPropagation();
              setIsOption((prev) => ({
                statusFor,
                isOpen: !prev.isOpen,
              }));
              isOptionAdditional?.();
            }}
          >
            Manage Options
          </ButtonWithIcon>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
